import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { clearAuthToken } from '@/lib/api';
import { toast } from 'sonner';
import { LogOut, Users, Trophy, TrendingUp, AlertCircle, UserPlus, Settings, Calendar, ChevronRight, X, Filter, ChevronDown, ChevronUp, Check, Pencil, Instagram, Twitter, Youtube, Play, ExternalLink, Target, Clock, Copy, Info } from 'lucide-react';
import EditProfileModal from '@/components/EditProfileModal';
import PlayerProfileEditModal from '@/components/PlayerProfileEditModal';
import GameAnalysisModal from '@/components/GameAnalysisModal';

// Helper to get optimized Cloudinary avatar URL
const getOptimizedAvatarUrl = (url) => {
  if (!url) return null;
  if (url.includes('cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/w_256,h_256,c_fill,g_face,q_auto,f_auto/');
  }
  return url;
};

// Helper to extract YouTube video ID and get thumbnail
const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
  }
  return null;
};

// Helper to validate URL
const isValidUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export default function PlayerDashboard({ user, insideLayout }) {
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [stats, setStats] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditPlayerProfile, setShowEditPlayerProfile] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [showAdvancedStats, setShowAdvancedStats] = useState(false);
  const [editModalTab, setEditModalTab] = useState('details'); // 'details' or 'social'
  const [lastPracticeSession, setLastPracticeSession] = useState(null);
  
  // Filter state
  const [selectedTeams, setSelectedTeams] = useState([]); // Empty = All Teams
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [gameLimit, setGameLimit] = useState(null); // null = All Games, 5, or 10
  const [resultFilter, setResultFilter] = useState(null); // null = All, 'W', or 'L'
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [analysisGame, setAnalysisGame] = useState(null);
  
  const navigate = useNavigate();

  // Open edit modal with specific tab
  const openEditModal = (tab) => {
    setEditModalTab(tab);
    setShowEditPlayerProfile(true);
  };

  useEffect(() => {
    fetchPlayerProfile();
    fetchLastPracticeSession();
  }, []);
  
  // Refetch stats when filters change
  useEffect(() => {
    if (profileData?.has_profile && profileData.profiles.length > 0) {
      fetchFilteredStats();
    }
  }, [selectedTeams, selectedSeason, gameLimit, resultFilter]);
  
  // Check if any filters are applied
  useEffect(() => {
    setFiltersApplied(selectedTeams.length > 0 || selectedSeason !== 'all' || gameLimit !== null || resultFilter !== null);
  }, [selectedTeams, selectedSeason, gameLimit, resultFilter]);

  const fetchLastPracticeSession = async () => {
    try {
      const response = await api.get('/practice/sessions?limit=1');
      if (response.data && response.data.length > 0) {
        setLastPracticeSession(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch practice sessions:', error);
    }
  };

  const fetchPlayerProfile = async () => {
    try {
      const response = await api.get('/player/profile');
      setProfileData(response.data);

      // If player has a profile, fetch additional data
      if (response.data.has_profile && response.data.profiles.length > 0) {
        const profile = response.data.profiles[0];
        
        // For team-based profiles, fetch team details
        // For fan profiles, team_id is null - use profile data directly
        if (profile.team_id) {
          try {
            const teamResponse = await api.get(`/teams/${profile.team_id}`);
            setTeamData(teamResponse.data);
          } catch (teamError) {
            console.error('Failed to fetch team:', teamError);
            // Use profile's team_name as fallback
            setTeamData({ name: profile.team_name, level: profile.team_level || '' });
          }
        } else {
          // Fan profile - use embedded team info with default purple theme
          setTeamData({ 
            name: profile.team_name || 'Unknown Team', 
            level: profile.team_level || '',
            is_fan_profile: true,
            theme_color: '#8B5CF6' // Purple for fan-tracked profiles
          });
        }
        
        // Fetch player stats and game history
        try {
          const statsResponse = await api.get(`/player/stats/${profile.player_id}`);
          setStats(statsResponse.data.averages);
          setGameHistory(statsResponse.data.game_history || []);
          setAvailableTeams(statsResponse.data.available_teams || []);
        } catch (statsError) {
          console.error('Failed to fetch player stats:', statsError);
          // Stats might not exist yet - that's ok
          setStats({ ppg: 0, rpg: 0, apg: 0, spg: 0, bpg: 0 });
          setGameHistory([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch player profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFilteredStats = async () => {
    if (!profileData?.profiles?.[0]) return;
    
    const profile = profileData.profiles[0];
    const params = new URLSearchParams();
    
    if (selectedTeams.length > 0) {
      params.append('team_ids', selectedTeams.join(','));
    }
    if (gameLimit) {
      params.append('game_limit', gameLimit.toString());
    }
    if (resultFilter) {
      params.append('result_filter', resultFilter);
    }
    
    try {
      const queryString = params.toString();
      const url = `/player/stats/${profile.player_id}${queryString ? `?${queryString}` : ''}`;
      const statsResponse = await api.get(url);
      setStats(statsResponse.data.averages);
      const history = statsResponse.data.game_history || [];
      // Apply client-side season filter
      if (selectedSeason !== 'all') {
        setGameHistory(history.filter(g => g.season_name === selectedSeason));
      } else {
        setGameHistory(history);
      }
    } catch (error) {
      console.error('Failed to fetch filtered stats:', error);
    }
  };
  
  // Derive available seasons from game history
  const availableSeasons = useMemo(() => {
    const names = [...new Set(gameHistory.map(g => g.season_name).filter(Boolean))];
    return names.sort();
  }, [gameHistory]);
  
  const clearAllFilters = () => {
    setSelectedTeams([]);
    setSelectedSeason('all');
    setGameLimit(null);
    setResultFilter(null);
  };
  
  const handleTeamFilterChange = (val) => {
    if (val === 'all') {
      setSelectedTeams([]);
    } else {
      setSelectedTeams([val]);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      clearAuthToken();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      clearAuthToken();
      navigate('/login');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className={insideLayout ? "flex items-center justify-center py-20" : "min-h-screen flex items-center justify-center bg-white"}>
        <div className="animate-spin h-12 w-12 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If player hasn't claimed a profile yet, show prompt to join team
  if (!profileData?.has_profile) {
    return (
      <div className={insideLayout ? "" : "min-h-screen bg-gradient-to-b from-white to-slate-50"}>
        {!insideLayout && (
        <div className="sticky top-0 z-40" style={{ backgroundColor: '#13314b' }}>
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4" style={{ width: '15%', minWidth: '120px' }}>
                <img 
                  src="/smarter-hoops-wordmark.png" 
                  alt="Smarter Hoops" 
                  className="w-full h-auto"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => setShowEditProfile(true)}
                  variant="ghost"
                  data-testid="edit-profile-button"
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <Settings className="w-5 h-5" />
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  data-testid="logout-button"
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Welcome Section */}
        <div className="max-w-2xl mx-auto px-4 pt-5 pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Welcome, {user?.name || 'Player'}</h2>
              <span className="inline-block mt-1 text-sm px-4 py-1 rounded-full font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", backgroundColor: '#fff7ed', color: '#c2410c' }} data-testid="user-role-badge">Player</span>
            </div>
            {currentUser?.athlete_id && (
              <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg" style={{ backgroundColor: '#13314b' }} data-testid="player-athlete-id-badge">
                <span className="text-white text-sm">Athlete ID: <strong className="font-bold tracking-wider">{currentUser.athlete_id}</strong></span>
                <button
                  onClick={() => { navigator.clipboard.writeText(currentUser.athlete_id); toast.success('Athlete ID copied!'); }}
                  className="text-white/60 hover:text-white transition-colors"
                  data-testid="copy-player-athlete-id"
                  title="Copy Athlete ID"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4 md:p-8">
          {/* Connect your profile prompt */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6" data-testid="connect-profile-prompt">
            <div className="flex items-start gap-3">
              <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Connect your profile</h3>
                <p className="text-sm text-slate-600">Join a team with a coach's join code, or share your Athlete ID with a parent/fan so they can link your account from their dashboard.</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8 text-center">
            <div className="inline-block p-4 bg-amber-100 rounded-full mb-4">
              <AlertCircle className="w-12 h-12 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold font-heading uppercase tracking-tight text-slate-900 mb-2">
              Join Your Team
            </h2>
            <p className="text-slate-600 mb-6">
              You haven't joined a team yet. Enter your join code to claim your player profile and start tracking your stats.
            </p>
            <Button
              onClick={() => navigate('/onboarding/player')}
              data-testid="join-team-button"
              className="h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide shadow-md hover:shadow-lg transition-all"
            >
              <Users className="w-5 h-5 mr-2" />
              Join a Team
            </Button>
          </div>
        </div>

        {/* Edit Profile Modal */}
        {!insideLayout && (
        <EditProfileModal
          open={showEditProfile}
          onOpenChange={setShowEditProfile}
          user={currentUser}
          onSuccess={(updatedUser) => setCurrentUser(updatedUser)}
        />
        )}
      </div>
    );
  }

  // Helper function to get color class based on stat type and value
  const getStatColor = (statType, value) => {
    if (value === null || value === undefined) return 'text-slate-900';
    
    const colorClasses = {
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      red: 'text-red-500'
    };

    switch (statType) {
      case '2pt':
        if (value >= 55) return colorClasses.green;
        if (value >= 45) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'fg':
        if (value >= 45) return colorClasses.green;
        if (value >= 35) return colorClasses.yellow;
        return colorClasses.red;
      
      case '3pt':
        if (value >= 30) return colorClasses.green;
        if (value >= 20) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'efg':
      case 'ts':
        if (value >= 50) return colorClasses.green;
        if (value >= 40) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'ftr':
        // FTR is displayed as percentage (e.g., 35.0 means 0.35)
        if (value >= 35) return colorClasses.green;
        if (value >= 20) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'to':
        // TO: lower is better (inverse)
        if (value <= 2.0) return colorClasses.green;
        if (value <= 3.5) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'rpg':
        if (value >= 6.0) return colorClasses.green;
        if (value >= 3.0) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'stl':
        if (value >= 2.0) return colorClasses.green;
        if (value >= 1.0) return colorClasses.yellow;
        return colorClasses.red;
      
      case 'blk':
        if (value >= 1.5) return colorClasses.green;
        if (value >= 0.5) return colorClasses.yellow;
        return colorClasses.red;
      
      default:
        return 'text-slate-900';
    }
  };

  // Player has a profile - show dashboard
  const profile = profileData.profiles[0];

  return (
    <div className={insideLayout ? "" : "min-h-screen bg-gradient-to-b from-white to-slate-50"}>
      {!insideLayout && (
      <div className="sticky top-0 z-40" style={{ backgroundColor: '#13314b' }}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4" style={{ width: '15%', minWidth: '100px' }}>
              <img 
                src="/smarter-hoops-wordmark.png" 
                alt="Smarter Hoops" 
                className="w-full h-auto"
              />
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button
                onClick={() => navigate('/practice')}
                data-testid="practice-session-button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 sm:px-4"
              >
                <Target className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Practice</span>
              </Button>
              <Button
                onClick={() => navigate('/onboarding/player')}
                variant="outline"
                data-testid="join-another-team-button"
                className="text-white border-white/30 hover:bg-white/10 hover:text-white px-2 sm:px-4"
              >
                <UserPlus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Join Another Team</span>
              </Button>
              <Button
                onClick={() => setShowEditProfile(true)}
                variant="ghost"
                data-testid="edit-profile-button"
                className="text-white/70 hover:text-white hover:bg-white/10 px-2"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                data-testid="logout-button"
                className="text-white/70 hover:text-white hover:bg-white/10 px-2"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Welcome Section */}
      <div className="max-w-7xl mx-auto px-4 pt-5 pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Welcome, {user?.name || playerProfile?.name || 'Player'}</h2>
            <span className="inline-block mt-1 text-xs px-3 py-0.5 rounded-full font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", backgroundColor: '#fff7ed', color: '#c2410c' }} data-testid="user-role-badge">Player</span>
          </div>
          {currentUser?.athlete_id && (
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg" style={{ backgroundColor: '#13314b' }} data-testid="player-athlete-id-badge">
              <span className="text-white text-sm">Athlete ID: <strong className="font-bold tracking-wider">{currentUser.athlete_id}</strong></span>
              <button
                onClick={() => { navigator.clipboard.writeText(currentUser.athlete_id); toast.success('Athlete ID copied!'); }}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="copy-player-athlete-id"
                title="Copy Athlete ID"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Connect Your Profile Prompt */}
        {currentUser?.athlete_id && (!availableTeams || availableTeams.length === 0) && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="connect-profile-prompt">
            <Info className="w-8 h-8 text-blue-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 mb-1">Connect your profile</h3>
              <p className="text-sm text-slate-600">Join a team to start tracking stats, or share your Athlete ID with a parent so they can link your account from their dashboard.</p>
            </div>
            <Button
              onClick={() => navigate('/onboarding/player')}
              data-testid="prompt-join-team-button"
              className="bg-slate-900 hover:bg-slate-800 text-white px-5 flex-shrink-0"
            >
              <Users className="w-4 h-4 mr-2" /> Join a Team
            </Button>
          </div>
        )}

        {/* Calculate highlight count */}
        {(() => {
          const highlightUrls = [
            profile.highlight_video_url_1,
            profile.highlight_video_url_2,
            profile.highlight_video_url_3
          ].filter(url => isValidUrl(url));
          const highlightCount = highlightUrls.length;
          const hasSocialLinks = isValidUrl(profile.instagram_url) || isValidUrl(profile.twitter_url) || isValidUrl(profile.youtube_url);
          
          return (
            <>
        {/* Mobile: Single Card Layout (unchanged) */}
        <div className="md:hidden bg-white border border-slate-200 rounded-xl shadow-sm mb-6 p-5 relative">
          <button
            onClick={() => openEditModal('details')}
            className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="edit-player-profile-button-mobile"
            title="Edit profile"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-4">
            {/* Avatar */}
            {profile.profile_photo_url ? (
              <img 
                src={getOptimizedAvatarUrl(profile.profile_photo_url)} 
                alt={profile.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md flex-shrink-0"
              />
            ) : (
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white shadow-md"
                style={{
                  backgroundColor: teamData?.theme_color ? `${teamData.theme_color}15` : '#f1f5f9',
                  color: teamData?.theme_color || '#334155'
                }}
              >
                <span className="text-2xl font-bold">
                  {profile.jersey_number || profile.name?.charAt(0) || '#'}
                </span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900 truncate">
                {profile.name}
              </h2>
              <p className="text-slate-500 text-sm">
                {profile.team_name} • #{profile.jersey_number || '--'}
              </p>
              {/* Mobile: Social icons + Highlights pill */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {isValidUrl(profile.twitter_url) && (
                  <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer"
                    className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700 transition-colors">
                    <Twitter className="w-3.5 h-3.5" />
                  </a>
                )}
                {isValidUrl(profile.instagram_url) && (
                  <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer"
                    className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700 transition-colors">
                    <Instagram className="w-3.5 h-3.5" />
                  </a>
                )}
                {isValidUrl(profile.youtube_url) && (
                  <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer"
                    className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700 transition-colors">
                    <Youtube className="w-3.5 h-3.5" />
                  </a>
                )}
                {highlightCount > 0 && (
                  <button
                    onClick={() => document.getElementById('highlights-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold hover:bg-slate-200 transition-colors"
                    data-testid="mobile-highlights-pill"
                  >
                    <Play className="w-3 h-3" fill="currentColor" />
                    Highlights • {highlightCount}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop: Two Adjacent Cards - Equal 50/50 Split with matched heights */}
        <div className="hidden md:grid md:grid-cols-2 gap-4 mb-6">
          {/* Left Card - Player Info (top) + Filters (bottom) */}
          <div className="relative bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col">
            <button
              onClick={() => openEditModal('details')}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors z-10"
              data-testid="edit-player-details-button"
              title="Edit player details"
            >
              <Pencil className="w-4 h-4" />
            </button>
            
            {/* Top Half - Player Info */}
            <div className="flex items-center gap-5 pb-4 border-b border-slate-100">
              {/* Avatar */}
              {profile.profile_photo_url ? (
                <img 
                  src={getOptimizedAvatarUrl(profile.profile_photo_url)} 
                  alt={profile.name}
                  className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 border-4 border-white shadow-lg"
                style={{
                  display: profile.profile_photo_url ? 'none' : 'flex',
                  backgroundColor: teamData?.theme_color ? `${teamData.theme_color}15` : '#f1f5f9',
                  color: teamData?.theme_color || '#334155'
                }}
              >
                <span className="text-3xl font-bold">
                  {profile.jersey_number || profile.name?.charAt(0) || '#'}
                </span>
              </div>
              
              {/* Player Details */}
              <div className="min-w-0">
                <h2 className="text-xl font-bold font-heading uppercase tracking-tight text-slate-900 truncate">
                  {profile.name}
                </h2>
                <p className="text-slate-500 text-sm mt-0.5">
                  {profile.team_name} • #{profile.jersey_number || '--'}
                </p>
              </div>
            </div>
            
            {/* Bottom Half - Last Practice Session */}
            <div className="pt-4 flex-1 flex flex-col justify-center">
              {lastPracticeSession ? (
                <button
                  onClick={() => navigate('/practice')}
                  className="w-full text-left hover:bg-slate-50 rounded-lg p-3 -m-3 transition-colors group"
                  data-testid="last-practice-link"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                      <Target className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="font-semibold uppercase tracking-wider">Last Practice</span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(lastPracticeSession.started_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center bg-slate-50 rounded-lg py-2 group-hover:bg-slate-100 transition-colors">
                      <p className="text-lg font-bold text-slate-900">
                        {lastPracticeSession.two_pt_attempted > 0 
                          ? `${Math.round((lastPracticeSession.two_pt_made / lastPracticeSession.two_pt_attempted) * 100)}%`
                          : '-'}
                      </p>
                      <p className="text-[10px] text-slate-400 uppercase">
                        2PT <span className="text-slate-300">({lastPracticeSession.two_pt_made}/{lastPracticeSession.two_pt_attempted})</span>
                      </p>
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg py-2 group-hover:bg-slate-100 transition-colors">
                      <p className="text-lg font-bold text-slate-900">
                        {lastPracticeSession.three_pt_attempted > 0 
                          ? `${Math.round((lastPracticeSession.three_pt_made / lastPracticeSession.three_pt_attempted) * 100)}%`
                          : '-'}
                      </p>
                      <p className="text-[10px] text-slate-400 uppercase">
                        3PT <span className="text-slate-300">({lastPracticeSession.three_pt_made}/{lastPracticeSession.three_pt_attempted})</span>
                      </p>
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg py-2 group-hover:bg-slate-100 transition-colors">
                      <p className="text-lg font-bold text-slate-900">
                        {lastPracticeSession.ft_attempted > 0 
                          ? `${Math.round((lastPracticeSession.ft_made / lastPracticeSession.ft_attempted) * 100)}%`
                          : '-'}
                      </p>
                      <p className="text-[10px] text-slate-400 uppercase">
                        FT <span className="text-slate-300">({lastPracticeSession.ft_made}/{lastPracticeSession.ft_attempted})</span>
                      </p>
                    </div>
                  </div>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/practice')}
                  className="w-full text-left hover:bg-slate-50 rounded-lg p-3 -m-3 transition-colors"
                  data-testid="start-practice-link"
                >
                  <div className="flex items-center gap-2 text-slate-400">
                    <Target className="w-4 h-4" />
                    <span className="text-sm">No practice sessions yet</span>
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Right Card - Socials + Highlight */}
          <div className="relative bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col items-center justify-center">
            <button
              onClick={() => openEditModal('social')}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors z-10"
              data-testid="edit-socials-button"
              title="Edit socials & highlights"
            >
              <Pencil className="w-4 h-4" />
            </button>
            
            {/* Social Icons Row */}
            {hasSocialLinks && (
              <div className="flex items-center gap-3 mb-4">
                {isValidUrl(profile.twitter_url) && (
                  <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700 transition-colors"
                    title="Twitter/X"
                    data-testid="twitter-link">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {isValidUrl(profile.instagram_url) && (
                  <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700 transition-colors"
                    title="Instagram"
                    data-testid="instagram-link">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {isValidUrl(profile.youtube_url) && (
                  <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700 transition-colors"
                    title="YouTube"
                    data-testid="youtube-link">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
            
            {/* Featured Highlight Thumbnail - only if highlights exist */}
            {highlightCount > 0 && (
              <div className="flex flex-col items-center w-full">
                <a
                  href={profile.highlight_video_url_1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative rounded-lg overflow-hidden w-full aspect-video bg-slate-900 hover:ring-2 hover:ring-slate-400 transition-all shadow-md"
                  data-testid="featured-highlight"
                >
                  {getYouTubeThumbnail(profile.highlight_video_url_1) ? (
                    <img 
                      src={getYouTubeThumbnail(profile.highlight_video_url_1)} 
                      alt="Featured Highlight"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900">
                      <Play className="w-10 h-10 text-white/40" />
                    </div>
                  )}
                  {/* Centered Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-slate-900 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </a>
                {/* More highlights link */}
                {highlightCount > 1 && (
                  <button
                    onClick={() => document.getElementById('highlights-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="mt-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    data-testid="more-highlights-link"
                  >
                    +{highlightCount - 1} more highlight{highlightCount > 2 ? 's' : ''}
                  </button>
                )}
              </div>
            )}
            
            {/* Empty state when no socials and no highlights */}
            {!hasSocialLinks && highlightCount === 0 && (
              <p className="text-sm text-slate-400 text-center">
                Add socials & highlights
              </p>
            )}
          </div>
        </div>

        {/* Filters Section - All Screen Sizes */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Team Dropdown */}
            {availableTeams.length > 1 && (
              <Select value={selectedTeams.length === 1 ? selectedTeams[0] : 'all'} onValueChange={handleTeamFilterChange}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="team-filter-dropdown">
                  <SelectValue placeholder="All Teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {availableTeams.map(team => (
                    <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Season Dropdown */}
            {availableSeasons.length > 1 && (
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="season-filter-dropdown">
                  <SelectValue placeholder="All Seasons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Seasons</SelectItem>
                  {availableSeasons.map(sn => (
                    <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Divider */}
            {(availableTeams.length > 1 || availableSeasons.length > 1) && (
              <div className="hidden sm:block h-8 w-px bg-slate-200" />
            )}
            
            {/* Game Limit Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Games</span>
              <div className="flex rounded-lg overflow-hidden border border-slate-200">
                <button
                  onClick={() => setGameLimit(null)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    gameLimit === null ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="game-filter-all"
                >
                  All
                </button>
                <button
                  onClick={() => setGameLimit(5)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                    gameLimit === 5 ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="game-filter-5"
                >
                  Last 5
                </button>
                <button
                  onClick={() => setGameLimit(10)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                    gameLimit === 10 ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="game-filter-10"
                >
                  Last 10
                </button>
              </div>
            </div>
            
            {/* Divider */}
            <div className="hidden sm:block h-8 w-px bg-slate-200" />
            
            {/* Result Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Result</span>
              <div className="flex rounded-lg overflow-hidden border border-slate-200">
                <button
                  onClick={() => setResultFilter(null)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    resultFilter === null ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="result-filter-all"
                >
                  All
                </button>
                <button
                  onClick={() => setResultFilter('W')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                    resultFilter === 'W' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="result-filter-wins"
                >
                  Wins
                </button>
                <button
                  onClick={() => setResultFilter('L')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-slate-200 ${
                    resultFilter === 'L' ? 'bg-red-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="result-filter-losses"
                >
                  Losses
                </button>
              </div>
            </div>
            
            {/* Filters Applied Indicator + Clear */}
            {filtersApplied && (
              <>
                <div className="hidden sm:block h-8 w-px bg-slate-200" />
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-xs font-medium text-orange-600">
                    <Filter className="w-3 h-3" />
                    Filters applied
                  </span>
                  <button
                    onClick={clearAllFilters}
                    className="text-xs font-medium text-slate-500 hover:text-slate-700 underline"
                    data-testid="clear-filters"
                  >
                    Clear all
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards - Row 1: PPG | APG | 2PT% | 3PT% */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-ppg">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>PPG</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0D1B26' }}>
              {stats?.ppg?.toFixed(1) || '0.0'}
            </p>
          </div>

          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-apg">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>APG</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0D1B26' }}>
              {stats?.apg?.toFixed(1) || '0.0'}
            </p>
          </div>

          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-2pt">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>2PT%</p>
            <p className={`text-[28px] font-bold tracking-tight leading-none`}
              style={{ fontFamily: "'Barlow Condensed', sans-serif", color: stats?.two_pct !== null && stats?.two_pct !== undefined ? '#0D1B26' : '#0D1B26' }}>
              {stats?.two_pct !== null && stats?.two_pct !== undefined ? `${stats.two_pct.toFixed(1)}%` : '–'}
            </p>
          </div>

          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-3pt">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>3PT%</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0D1B26' }}>
              {stats?.three_pct?.toFixed(1) || '0.0'}%
            </p>
          </div>
        </div>

        {/* Stats Cards - Row 2: RPG | STL | BLK | TO */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-rpg">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>RPG</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0D1B26' }}>
              {stats?.rpg?.toFixed(1) || '0.0'}
            </p>
          </div>

          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-spg">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>STL</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0D1B26' }}>
              {stats?.spg?.toFixed(1) || '0.0'}
            </p>
          </div>

          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-bpg">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>BLK</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0D1B26' }}>
              {stats?.bpg?.toFixed(1) || '0.0'}
            </p>
          </div>

          <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }} data-testid="stat-to">
            <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>TO</p>
            <p className="text-[28px] font-bold tracking-tight leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#E67E22' }}>
              {stats?.topg?.toFixed(1) || '0.0'}
            </p>
          </div>
        </div>

        {/* Advanced Stats Toggle */}
        <button
          onClick={() => setShowAdvancedStats(!showAdvancedStats)}
          className="flex items-center justify-center w-full py-2 mb-4 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          data-testid="toggle-advanced-stats"
        >
          {showAdvancedStats ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" />
              Hide Advanced Stats
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 mr-1" />
              Show Advanced Stats
            </>
          )}
        </button>

        {/* Stats Cards - Row 3: Advanced Stats (Toggleable): FG% | eFG% | TS% | FTR */}
        {showAdvancedStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 animate-in slide-in-from-top-2 duration-200">
            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="stat-fg">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">FG%</p>
              <p className={`text-4xl font-bold tracking-tighter tabular-nums ${getStatColor('fg', stats?.fg_pct)}`}>
                {stats?.fg_pct?.toFixed(1) || '0.0'}%
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="stat-efg">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">eFG%</p>
              <p className={`text-4xl font-bold tracking-tighter tabular-nums ${getStatColor('efg', stats?.efg_pct)}`}>
                {stats?.efg_pct?.toFixed(1) || '0.0'}%
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="stat-ts">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">TS%</p>
              <p className={`text-4xl font-bold tracking-tighter tabular-nums ${getStatColor('ts', stats?.ts_pct)}`}>
                {stats?.ts_pct?.toFixed(1) || '0.0'}%
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="stat-ftr">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">FTR</p>
              <p className={`text-4xl font-bold tracking-tighter tabular-nums ${getStatColor('ftr', stats?.ftr)}`}>
                {stats?.ftr?.toFixed(1) || '0.0'}
              </p>
            </div>
          </div>
        )}

        {/* Spacer before Game Log */}
        <div className="mb-4"></div>

        {/* Recent Games */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
              Games {filtersApplied && <span className="text-orange-600">({gameHistory.length} games filtered)</span>}
            </h2>
          </div>
          {gameHistory.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-slate-500">
                {filtersApplied ? 'No games match your filters' : 'No game history yet'}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {filtersApplied 
                  ? 'Try adjusting your filter criteria'
                  : 'Your stats will appear here after you play in a game'
                }
              </p>
              {filtersApplied && (
                <button
                  onClick={clearAllFilters}
                  className="mt-3 text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {gameHistory.map((game) => (
                <div 
                  key={game.game_id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`game-history-${game.game_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                        game.result === 'W' ? 'bg-emerald-500' : 'bg-red-500'
                      }`}>
                        {game.result}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">
                          {game.is_home ? 'vs' : '@'} {game.opponent_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {formatDate(game.date_time)} • {game.home_score}-{game.away_score}
                          {availableTeams.length > 1 && game.team_name && (
                            <span className="ml-1 px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">{game.team_name}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 ml-3">
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-900">{game.stats.pts}</p>
                        <p className="text-[10px] text-slate-500 uppercase">PTS</p>
                      </div>
                      <div className="text-center hidden sm:block">
                        <p className="text-lg font-bold text-slate-900">{game.stats.reb}</p>
                        <p className="text-[10px] text-slate-500 uppercase">REB</p>
                      </div>
                      <div className="text-center hidden sm:block">
                        <p className="text-lg font-bold text-slate-900">{game.stats.ast}</p>
                        <p className="text-[10px] text-slate-500 uppercase">AST</p>
                      </div>
                      <button
                        onClick={() => setAnalysisGame(game)}
                        className="hidden sm:inline-flex px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                        data-testid={`game-analysis-btn-${game.game_id}`}
                      >
                        Game Analysis & Practice Plan
                      </button>
                    </div>
                  </div>
                  {/* Mobile: compact row with REB, AST and analysis button */}
                  <div className="flex sm:hidden items-center justify-between mt-2 pl-12">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500"><span className="font-bold text-slate-700">{game.stats.reb}</span> REB</span>
                      <span className="text-xs text-slate-500"><span className="font-bold text-slate-700">{game.stats.ast}</span> AST</span>
                    </div>
                    <button
                      onClick={() => setAnalysisGame(game)}
                      className="px-2 py-1 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                      data-testid={`game-analysis-btn-mobile-${game.game_id}`}
                    >
                      Analysis
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Highlights Section - only show when highlights exist */}
        {highlightCount > 0 && (
          <div id="highlights-section" className="mt-8 scroll-mt-8">
            <h3 className="text-xl font-bold font-heading uppercase tracking-tight text-slate-900 mb-4">
              <Play className="w-5 h-5 inline mr-2" />
              Highlights
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {highlightUrls.map((videoUrl, index) => {
                const ytThumb = getYouTubeThumbnail(videoUrl);
                return (
                  <a
                    key={index}
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative bg-slate-900 rounded-lg overflow-hidden aspect-video hover:ring-2 hover:ring-slate-400 transition-all shadow-md"
                    data-testid={`highlight-video-card-${index + 1}`}
                  >
                    {ytThumb ? (
                      <img 
                        src={ytThumb} 
                        alt={`Highlight ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900">
                        <div className="text-center">
                          <Play className="w-10 h-10 text-white/40 mx-auto mb-2" />
                          <span className="text-xs text-white/50 uppercase tracking-wider">Watch Highlight</span>
                        </div>
                      </div>
                    )}
                    {/* Centered Play overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all">
                        <Play className="w-7 h-7 text-slate-900 ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}
            </>
          );
        })()}
      </div>

      {/* Edit Profile Modal (Account Settings) */}
      {!insideLayout && (
      <EditProfileModal
        open={showEditProfile}
        onOpenChange={setShowEditProfile}
        user={currentUser}
        onSuccess={(updatedUser) => setCurrentUser(updatedUser)}
      />
      )}

      {/* Edit Player Profile Modal (Jersey, Photo, Social, Videos) */}
      <PlayerProfileEditModal
        open={showEditPlayerProfile}
        onOpenChange={setShowEditPlayerProfile}
        profile={profileData?.profiles?.[0]}
        onSuccess={() => fetchPlayerProfile()}
        initialTab={editModalTab}
      />

      <GameAnalysisModal
        open={!!analysisGame}
        onOpenChange={(open) => { if (!open) setAnalysisGame(null); }}
        game={analysisGame}
        ats={stats || {}}
        opponentName={analysisGame?.opponent_name}
        gameDate={analysisGame?.date_time ? formatDate(analysisGame.date_time) : ''}
      />
    </div>
  );
}
